import { AlertCircle, CalendarDays, CheckCircle2, Clock, Loader2, Plus, RefreshCw, Search, Sparkles, X, XCircle } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/theme/default.css';
import { toast } from 'react-hot-toast';
import {
  getGenerationJobStatus,
  JobProgress,
  JobStatus,
  listGenerationJobs,
  startAgentInvoiceGeneration,
  startCustomAgentInvoiceGeneration,
} from '../api/invoice/asyncInvoiceGeneration';
import { AgentInvoiceError, GeneratedAgentInvoice } from '../api/invoice/generateAgentInvoices';
import { BtnDefault, BtnPrimary } from '../components/common/Buttons';
import InvoiceCard from '../components/common/InvoiceCard';
import Modal from '../components/common/Modal';
import SearchInput from '../components/common/SearchInput';
import Select, { SelectOption } from '../components/common/Select';
import InvoiceForm from '../components/forms/InvoiceForm';
import { LoadingErrorState } from '../components/LoadingErrorState';
import { sortByOptions } from '../constants';
import { fetchAgentList, fetchInvoices } from '../hooks/queries';
import { useAuthStore } from '../state/auth';
import { IAgent, invalidAgentStatusesForInvoiceGeneration } from '../types/agent';
import { Invoice } from '../types/invoice';
import { formatDate, formatToYMD } from '../utils';

type GenerationMode = 'monthly' | 'custom';

interface AsyncGenerationResult {
  mode: GenerationMode;
  data: {
    period: {
      label: string;
      startDate?: string;
      endDate?: string;
      totalDays?: number;
    };
    summary: {
      totalAgentsProcessed: number;
      successfullyGenerated: number;
      errors: number;
      totalInvoiceAmount: number;
    };
    invoices: GeneratedAgentInvoice[];
    errors: AgentInvoiceError[];
    filter?: {
      type: 'all' | 'specific_agents' | 'specific_clients';
      agentIds?: string[];
    };
  };
}

const MAX_CUSTOM_RANGE_DAYS = 365;

const getDefaultCustomRange = () => {
  const end = moment().endOf('day').toDate();
  const start = moment(end).subtract(30, 'days').startOf('day').toDate();

  return {
    startDate: formatToYMD(start),
    endDate: formatToYMD(end),
  };
};

const AgentInvoice: React.FC = () => {
  const { dateRange, setDateRange } = useAuthStore();

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState<AsyncGenerationResult | null>(null);
  const [generationMode, setGenerationMode] = useState<GenerationMode>('monthly');
  const [activeGenerationMode, setActiveGenerationMode] = useState<GenerationMode>('monthly');
  const [customRange, setCustomRange] = useState(getDefaultCustomRange);
  const [customRangeError, setCustomRangeError] = useState<string | null>(null);
  const [customRegenerate, setCustomRegenerate] = useState(true);
  const [selectedAgentsForCustom, setSelectedAgentsForCustom] = useState<SelectOption[]>([]);

  // Async generation state
  const [generationJobId, setGenerationJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<'pending' | 'processing' | 'completed' | 'failed' | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Generation history state
  const [generationHistory, setGenerationHistory] = useState<JobStatus[]>([]);
  const [customGenerationJobs, setCustomGenerationJobs] = useState<JobStatus[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingCustomHistory, setIsLoadingCustomHistory] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [selectedMonthForRegeneration, setSelectedMonthForRegeneration] = useState<{month: number, year: number, existingJobId?: string} | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [sortBy, setSortBy] = useState<
    'billDate-asc' | 'billDate-desc' | 'totalAmount-asc' | 'totalAmount-desc'
  >('billDate-desc');

  const [page, setPage] = useState(1);
  const limit = 15;

  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: new Date(dateRange[0].startDate),
      endDate: new Date(dateRange[0].endDate),
      key: 'selection',
    },
  ]);

  const resetGenerationModalState = () => {
    setGenerationResult(null);
    setGenerationJobId(null);
    setJobStatus(null);
    setJobProgress(null);
    stopPolling();
    setSelectedYear(new Date().getFullYear());
    setSelectedMonth(new Date().getMonth() + 1);
    setGenerationMode('monthly');
    setActiveGenerationMode('monthly');
    setCustomRange(getDefaultCustomRange());
    setCustomRegenerate(false);
    setSelectedAgentsForCustom([]);
    setCustomRangeError(null);
  };

  const { data: agentListData, isLoading: isAgentListLoading } = fetchAgentList({
    enabled: isGenerateModalOpen,
  });

  const agentOptions = useMemo(() => {
    if (!agentListData?.agents?.length) return [];

    return agentListData.agents
      .filter((agent): agent is IAgent & { _id: string } => Boolean(agent._id) && !invalidAgentStatusesForInvoiceGeneration.includes(agent.status))
      .map((agent) => {
        const primary =
          agent.name || agent.personalEmail || agent.kimId || 'Unnamed Agent';
        const secondary =
          agent.personalEmail && agent.personalEmail !== primary
            ? ` (${agent.personalEmail})`
            : '';
        return {
          value: agent._id!,
          label: `${primary}${secondary}`,
        };
      });
  }, [agentListData]);

  const agentNameMap = useMemo(() => {
    if (!agentListData?.agents?.length) return {};
    return agentListData.agents.reduce<Record<string, string>>((acc, agent) => {
      if (agent._id) {
        acc[agent._id] =
          agent.name || agent.personalEmail || agent.kimId || agent._id;
      }
      return acc;
    }, {});
  }, [agentListData]);

  const resultPeriodDetails = generationResult?.data.period;
  const resultFilterLabel = generationResult
    ? generationResult.data.filter?.type === 'specific_agents'
      ? `${generationResult.data.filter?.agentIds?.length || 0} specific agents`
      : 'All agents'
    : '';
  const resultFilterNames =
    generationResult?.data.filter?.type === 'specific_agents'
      ? (generationResult.data.filter?.agentIds || [])
          .slice(0, 3)
          .map((id) => agentNameMap[id] || id)
      : [];

  const selectedAgentIds = selectedAgentsForCustom.map((agent) => agent.value);

  const customRangeDays = useMemo(() => {
    if (!customRange.startDate || !customRange.endDate) return 0;
    const start = moment(customRange.startDate);
    const end = moment(customRange.endDate);
    return end.diff(start, 'days') + 1;
  }, [customRange]);

  const isCustomRangeValid =
    Boolean(customRange.startDate && customRange.endDate) &&
    !customRangeError &&
    customRangeDays > 0;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    const { startDate, endDate } = tempDateRange[0];
    if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
      setDateRange([
        {
          startDate: formatToYMD(startDate),
          endDate: formatToYMD(endDate),
          key: 'selection',
        },
      ]);
      setPage(1);
      setIsDatePickerOpen(false);
    }
  }, [tempDateRange]);

  useEffect(() => {
    if (!customRange.startDate || !customRange.endDate) {
      setCustomRangeError(null);
      return;
    }

    const start = moment(customRange.startDate);
    const end = moment(customRange.endDate);

    if (end.isBefore(start)) {
      setCustomRangeError('End date must be on or after start date');
      return;
    }

    if (end.diff(start, 'days') + 1 > MAX_CUSTOM_RANGE_DAYS) {
      setCustomRangeError(
        `Date range cannot exceed ${MAX_CUSTOM_RANGE_DAYS} days`
      );
      return;
    }

    setCustomRangeError(null);
  }, [customRange]);

  const [sortField, sortOrder] = sortBy.split('-');

  const {
    data: invoicesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = fetchInvoices({
    invoiceFor: 'agent',
    startDate: formatToYMD(dateRange[0]?.startDate),
    endDate: formatToYMD(dateRange[0]?.endDate),
    search: debouncedSearch,
    page,
    limit,
    sortBy: sortField,
    sortOrder: sortOrder,
  });

  const invoices = invoicesData?.invoices || [];
  const pagination = invoicesData?.pagination;

  // Fetch generation history when modal opens
  useEffect(() => {
    if (isGenerateModalOpen) {
      fetchGenerationHistory();
    }
  }, [isGenerateModalOpen, selectedYear]);

  useEffect(() => {
    if (isGenerateModalOpen) {
      fetchCustomGenerationHistory();
    }
  }, [isGenerateModalOpen]);

  const handleNewInvoice = () => {
    setEditingInvoice(null);
    setIsModalOpen(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsModalOpen(true);
  };

  const clearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setPage(1);
  };

  // Fetch generation history for the selected year
  const fetchGenerationHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await listGenerationJobs({
        year: selectedYear,
        invoiceType: 'agent',
        limit: 100 // Get all jobs for the year
      });
      // console.log('Generation history response:', response); // Debug log
      setGenerationHistory(response.jobs || []);
    } catch (error) {
      console.error('Failed to fetch generation history:', error);
      toast.error('Failed to load generation history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchCustomGenerationHistory = async () => {
    setIsLoadingCustomHistory(true);
    try {
      const response = await listGenerationJobs({
        invoiceType: 'agent',
        limit: 50,
      });
      const customJobs = (response.jobs || []).filter(
        (job) => !job.month && !job.year
      );
      setCustomGenerationJobs(customJobs);
    } catch (error) {
      console.error('Failed to fetch custom generation history:', error);
      toast.error('Failed to load custom generation history');
    } finally {
      setIsLoadingCustomHistory(false);
    }
  };

  // Get generation status for a specific month
  const getMonthGenerationStatus = (month: number) => {
    const monthJobs = generationHistory.filter(job => {
      // Check both root level and period object for month/year
      const jobMonth = job.month || (typeof job.period === 'object' && job.period?.month);
      const jobYear = job.year || (typeof job.period === 'object' && job.period?.year);
      return jobMonth === month && jobYear === selectedYear;
    });

    if (monthJobs.length === 0) return null;

    // Get the most recent job - handle multiple date field locations
    return monthJobs.sort((a, b) => {
      const aDate = a.timing?.createdAt || a.createdAt || '';
      const bDate = b.timing?.createdAt || b.createdAt || '';
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    })[0];
  };

  const handleGenerateMonthlyInvoices = async (regenerate: boolean = false) => {
    setIsGenerating(true);
    setGenerationResult(null);
    setGenerationJobId(null);
    setJobStatus(null);
    setJobProgress(null);
    setActiveGenerationMode('monthly');

    try {
      // Start async generation with regenerate flag if needed
      const job = await startAgentInvoiceGeneration(selectedYear, selectedMonth, {
        regenerate: regenerate
      });
      setGenerationJobId(job.jobId);
      setJobStatus(job.status);

      toast.success(`Invoice ${regenerate ? 'regeneration' : 'generation'} started! Job ID: ${job.jobId.slice(-8)}`);

      // Start polling for status
      startPolling(job.jobId);

      // Clear regeneration state
      setShowRegenerateConfirm(false);
      setSelectedMonthForRegeneration(null);

    } catch (error: any) {
      if (error.status === 409) {
        // Job already in progress
        const existingJob = error.data?.data;
        toast('Generation already in progress for this period', { icon: '⚠️' });
        if (existingJob?.jobId) {
          setGenerationJobId(existingJob.jobId);
          startPolling(existingJob.jobId);
        }
      } else {
        toast.error(error.data?.message || error.message || 'Failed to start invoice generation');
        setIsGenerating(false);
      }
    }
  };

  const handleGenerateCustomInvoices = async () => {
    if (!customRange.startDate || !customRange.endDate) {
      toast.error('Please select a start and end date');
      return;
    }

    if (!isCustomRangeValid) {
      toast.error(customRangeError || 'Please fix the date range');
      return;
    }

    setIsGenerating(true);
    setGenerationResult(null);
    setGenerationJobId(null);
    setJobStatus(null);
    setJobProgress(null);
    setActiveGenerationMode('custom');

    try {
      const job = await startCustomAgentInvoiceGeneration({
        startDate: customRange.startDate,
        endDate: customRange.endDate,
        agentIds: selectedAgentIds.length ? selectedAgentIds : undefined,
        regenerate: customRegenerate,
      });

      setGenerationJobId(job.jobId);
      setJobStatus(job.status);

      toast.success(
        `Custom invoice generation started! Job ID: ${job.jobId.slice(-8)}`
      );

      startPolling(job.jobId);
    } catch (error: any) {
      if (error.status === 409) {
        const existingJob = error.data?.data;
        toast('Generation already in progress for this period', {
          icon: '⚠️',
        });
        if (existingJob?.jobId) {
          setGenerationJobId(existingJob.jobId);
          startPolling(existingJob.jobId);
        }
      } else {
        toast.error(
          error.data?.message || error.message || 'Failed to start invoice generation'
        );
        setIsGenerating(false);
      }
    }
  };

  const startPolling = (jobId: string) => {
    setIsPolling(true);

    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll every 2 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const status = await getGenerationJobStatus(jobId);

        setJobStatus(status.status);
        setJobProgress(status.progress);
        const hasMonthContext =
          Boolean(status.month) ||
          (typeof status.period === 'object' &&
            (status.period?.month || status.period?.monthName));
        const derivedMode: GenerationMode = hasMonthContext ? 'monthly' : 'custom';
        setActiveGenerationMode(derivedMode);

        if (status.status === 'completed') {
          // Generation completed
          stopPolling();

          const periodStartDate =
            status.startDate ||
            (typeof status.period === 'object'
              ? status.period?.startDate
              : undefined);
          const periodEndDate =
            status.endDate ||
            (typeof status.period === 'object'
              ? status.period?.endDate
              : undefined);
          const totalDays =
            (typeof status.period === 'object' && status.period?.totalDays) ||
            (periodStartDate && periodEndDate
              ? moment(periodEndDate).diff(moment(periodStartDate), 'days') + 1
              : undefined);

          const periodLabel =
            derivedMode === 'monthly'
              ? typeof status.period === 'string'
                ? status.period
                : status.period?.monthName ||
                  moment(periodStartDate || status.startDate).format('MMMM YYYY')
              : periodStartDate && periodEndDate
                ? `${moment(periodStartDate).format('DD MMM YYYY')} - ${moment(
                    periodEndDate
                  ).format('DD MMM YYYY')}`
                : 'Custom Range';

          const metadataFilterType =
            status.metadata?.filterType ||
            (status.metadata?.isFilteredGeneration ? 'specific_agents' : 'all');
          const metadataAgentIds =
            status.metadata?.agentIds?.map((id) => String(id)) || [];

          setGenerationResult({
            mode: derivedMode,
            data: {
              period: {
                label: periodLabel,
                startDate: periodStartDate,
                endDate: periodEndDate,
                totalDays,
              },
              summary: {
                totalAgentsProcessed:
                  status.summary?.totalProcessed || status.progress?.processed || 0,
                successfullyGenerated:
                  status.summary?.successfullyGenerated ||
                  status.progress?.successful ||
                  0,
                errors:
                  status.summary?.errors || status.progress?.failed || 0,
                totalInvoiceAmount:
                  status.totalAmount ||
                  status.results?.totalAmount ||
                  status.summary?.totalInvoiceAmount ||
                  0,
              },
              invoices:
                status.invoices || status.results?.generatedInvoices || [],
              errors: status.errors || status.results?.errors || [],
              filter:
                metadataFilterType === 'specific_agents'
                  ? {
                      type: 'specific_agents',
                      agentIds: metadataAgentIds,
                    }
                  : {
                      type: 'all',
                    },
            },
          });
          toast.success('Invoice generation completed!');
          refetch(); // Refresh invoice list
          fetchGenerationHistory();
          fetchCustomGenerationHistory();
          setIsGenerating(false);

        } else if (status.status === 'failed') {
          // Generation failed
          stopPolling();
          toast.error('Invoice generation failed. Check job details for more information.');
          setIsGenerating(false);
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling even if one request fails
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);


  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatJobDateRange = (job: JobStatus) => {
    const start =
      job.startDate ||
      (typeof job.period === 'object' ? job.period?.startDate : undefined);
    const end =
      job.endDate ||
      (typeof job.period === 'object' ? job.period?.endDate : undefined);

    if (!start || !end) return '—';
    return `${moment(start).format('DD MMM YYYY')} - ${moment(end).format('DD MMM YYYY')}`;
  };

  const getJobFilterLabel = (job: JobStatus) => {
    if (job.metadata?.filterType === 'specific_agents') {
      const count = job.metadata?.agentIds?.length || 0;
      return `Specific agents (${count})`;
    }
    return 'All agents';
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getJobStatusBadgeClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading || isFetching) {
    return (
      <LoadingErrorState
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        data={invoicesData}
      />
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex py-4 px-4 gap-4 items-center flex-shrink-0">
        <h1 className="text-2xl font-bold mr-auto text-gray-900 dark:text-gray-100">
          Agent Invoices
        </h1>

        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          onClear={clearSearch}
          placeholder="Search invoices..."
          width="300px"
        />

        <BtnDefault
          onClick={() => setIsGenerateModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Generate Invoices
        </BtnDefault>

        <BtnPrimary
          onClick={handleNewInvoice}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create
        </BtnPrimary>

        <div className="flex gap-2 items-center">
          {/* Date Range */}
          <div className="relative">
            <button
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-600 text-sm"
            >
              <CalendarDays className="w-4 h-4" />
              <span className="bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-md">
                {formatDate(dateRange[0]?.startDate)}
              </span>
              <span>-</span>
              <span className="bg-gray-100 dark:bg-zinc-800 px-2 py-1 rounded-md">
                {formatDate(dateRange[0]?.endDate)}
              </span>
            </button>

            {isDatePickerOpen && (
              <div className="absolute z-20 top-full right-0 mt-2">
                <DateRange
                  onChange={(item) => setTempDateRange([item.selection])}
                  ranges={tempDateRange}
                  editableDateInputs
                  moveRangeOnFirstSelection={false}
                  className="shadow-lg border border-gray-200 rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as any);
              setPage(1);
            }}
            className="pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 text-sm text-gray-800 dark:text-white"
          >
            {sortByOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Invoice List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {invoices.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-4">
            {invoices.map((invoice: Invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onEdit={handleEditInvoice}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No invoices found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="flex justify-center items-center gap-4 pt-2 border-t border-gray-200 dark:border-gray-700 dark:bg-zinc-900 flex-shrink-0">
          <button
            disabled={!pagination.hasPrevPage}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-4 py-1 text-sm border border-greyFifty rounded-md disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-blackTen">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            disabled={!pagination.hasNextPage}
            onClick={() =>
              setPage((p) => Math.min(p + 1, pagination.totalPages))
            }
            className="px-4 py-1 text-sm border border-greyFifty rounded-md disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Invoice Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        title={editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}
        contentClassName="p-0"
        width="fit-content"
      >
        <InvoiceForm
          invoice={editingInvoice}
          onSuccess={() => {
            setIsModalOpen(false);
            setEditingInvoice(null);
            toast.success(
              `Invoice ${editingInvoice ? 'updated' : 'created'} successfully`
            );
          }}
          onError={(error) => toast.error(error.message)}
          isEditing={!!editingInvoice}
          isAgent={true}
        />
      </Modal>

      {/* Generate Invoices Modal */}
      <Modal
        isOpen={isGenerateModalOpen}
        onRequestClose={() => {
          setIsGenerateModalOpen(false);
          resetGenerationModalState();
        }}
        title="Generate Agent Invoices"
        width="1200px"
      >
        <div className="p-6">
          {!generationResult && !jobStatus ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="inline-flex rounded-full bg-gray-100 dark:bg-zinc-800 p-1">
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      generationMode === 'monthly'
                        ? 'bg-white dark:bg-zinc-900 shadow text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                    onClick={() => setGenerationMode('monthly')}
                  >
                    Monthly Run
                  </button>
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      generationMode === 'custom'
                        ? 'bg-white dark:bg-zinc-900 shadow text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                    onClick={() => setGenerationMode('custom')}
                  >
                    Custom Range
                  </button>
                </div>
                {generationMode === 'monthly' ? (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select Year
                    </label>
                    <select
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(Number(e.target.value));
                        fetchGenerationHistory();
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-700 text-gray-800 dark:text-white"
                    >
                      {[2024, 2025, 2026].map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Generate invoices for any date range (max {MAX_CUSTOM_RANGE_DAYS} days) with optional agent filters.
                  </p>
                )}
              </div>

              {generationMode === 'monthly' ? (
                <>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                      Select Month to Generate
                    </h3>
                    {isLoadingHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-500">Loading generation history...</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Array.from({ length: 12 }, (_, i) => {
                          const month = i + 1;
                          const monthName = new Date(selectedYear, i).toLocaleDateString('en-US', { month: 'long' });
                          const monthJob = getMonthGenerationStatus(month);
                          const isSelected = selectedMonth === month;
                          const isCurrentMonth = new Date().getMonth() + 1 === month && new Date().getFullYear() === selectedYear;

                          return (
                            <div
                              key={month}
                              onClick={() => {
                                if (monthJob?.status === 'processing') {
                                  toast('Generation already in progress for this month', {
                                    icon: '⚠️',
                                  });
                                  return;
                                }
                                setSelectedMonth(month);
                                if (monthJob?.status === 'completed') {
                                  setSelectedMonthForRegeneration({
                                    month,
                                    year: selectedYear,
                                    existingJobId: monthJob._id || monthJob.jobId,
                                  });
                                } else {
                                  setSelectedMonthForRegeneration(null);
                                }
                              }}
                              className={`
                                relative p-4 rounded-lg border-2 cursor-pointer transition-all
                                ${isSelected
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                }
                                ${monthJob?.status === 'processing' ? 'opacity-50 cursor-not-allowed' : ''}
                              `}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {monthName}
                                </span>
                                {isCurrentMonth && (
                                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                                    Current
                                  </span>
                                )}
                              </div>

                              {monthJob ? (
                                <div className="space-y-2 text-xs">
                                  <div className="flex items-center gap-2">
                                    {monthJob.status === 'completed' && (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                                        <span className="text-green-600 dark:text-green-400">Generated</span>
                                      </>
                                    )}
                                    {monthJob.status === 'processing' && (
                                      <>
                                        <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />
                                        <span className="text-yellow-600 dark:text-yellow-400">
                                          Processing ({monthJob.progress?.percentage || 0}%)
                                        </span>
                                      </>
                                    )}
                                    {monthJob.status === 'failed' && (
                                      <>
                                        <XCircle className="w-4 h-4 text-red-500" />
                                        <span className="text-red-600 dark:text-red-400">Failed</span>
                                      </>
                                    )}
                                    {monthJob.status === 'pending' && (
                                      <>
                                        <Clock className="w-4 h-4 text-gray-400" />
                                        <span className="text-gray-600 dark:text-gray-400">Pending</span>
                                      </>
                                    )}
                                  </div>

                                  {monthJob.status === 'completed' && (
                                    <div className="text-gray-500 dark:text-gray-400">
                                      <div>{monthJob.summary?.successfullyGenerated || monthJob.results?.generatedInvoices?.length || monthJob.progress?.successful || 0} invoices</div>
                                      {(monthJob.timing?.completedAt || monthJob.completedAt || monthJob.processingCompletedAt) && (
                                        <div>{new Date(monthJob.timing?.completedAt || monthJob.completedAt || monthJob.processingCompletedAt || '').toLocaleDateString()}</div>
                                      )}
                                      {monthJob.requestedBy?.email && (
                                        <div className="truncate">{monthJob.requestedBy.email}</div>
                                      )}
                                    </div>
                                  )}

                                  {monthJob.status === 'processing' && monthJob.progress && (
                                    <div className="text-gray-500 dark:text-gray-400">
                                      <div>{monthJob.progress.processed} / {monthJob.progress.total}</div>
                                    </div>
                                  )}

                                  {monthJob.status === 'failed' && (
                                    <div className="text-gray-500 dark:text-gray-400">
                                      <div>{monthJob.summary?.errors || monthJob.results?.errors?.length || monthJob.progress?.failed || 0} errors</div>
                                    </div>
                                  )}

                                  {monthJob.status === 'completed' && isSelected && (
                                    <button
                                      className="mt-2 flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowRegenerateConfirm(true);
                                      }}
                                    >
                                      <RefreshCw className="w-3 h-3" />
                                      Regenerate
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  <Plus className="w-4 h-4 text-gray-400 mb-1" />
                                  Not generated
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 justify-between items-center">
                    <div className="text-sm text-gray-500">
                      {selectedMonthForRegeneration && (
                        <span className="text-yellow-600 dark:text-yellow-400">
                          ⚠️ This will replace existing invoices for{' '}
                          {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long' })}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setIsGenerateModalOpen(false);
                          resetGenerationModalState();
                        }}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-600"
                        disabled={isGenerating}
                      >
                        Cancel
                      </button>
                      <BtnPrimary
                        onClick={() => {
                          if (selectedMonthForRegeneration) {
                            setShowRegenerateConfirm(true);
                          } else {
                            handleGenerateMonthlyInvoices();
                          }
                        }}
                        disabled={isGenerating || !selectedMonth}
                        className="flex items-center gap-2"
                      >
                        {isGenerating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            {selectedMonthForRegeneration ? 'Regenerate' : 'Generate'}
                          </>
                        )}
                      </BtnPrimary>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customRange.startDate}
                        max={customRange.endDate || undefined}
                        onChange={(e) =>
                          setCustomRange((prev) => ({
                            ...prev,
                            startDate: e.target.value,
                          }))
                        }
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customRange.endDate}
                        min={customRange.startDate || undefined}
                        max={formatToYMD(new Date())}
                        onChange={(e) =>
                          setCustomRange((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                          }))
                        }
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                  {customRangeError && (
                    <p className="text-sm text-red-500">{customRangeError}</p>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Agent Filter
                    </label>
                    <Select<SelectOption>
                      isMulti
                      isClearable
                      isDisabled={isAgentListLoading}
                      options={agentOptions}
                      value={selectedAgentsForCustom}
                      onChange={(value) =>
                        setSelectedAgentsForCustom(value as SelectOption[])
                      }
                      placeholder="Process all agents (optional filter)"
                      classNamePrefix="react-select"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Leave empty to generate invoices for all agents.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-4 items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-zinc-800">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Date Range
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {customRangeDays > 0 ? `${customRangeDays} days` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Agents Targeted
                      </p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {selectedAgentIds.length
                          ? `${selectedAgentIds.length} selected`
                          : 'All agents'}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={customRegenerate}
                        onChange={(e) => setCustomRegenerate(e.target.checked)}
                      />
                      Regenerate existing invoices
                    </label>
                    <BtnPrimary
                      onClick={handleGenerateCustomInvoices}
                      disabled={!isCustomRangeValid || isGenerating}
                      className="flex items-center gap-2"
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Starting...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Start Custom Run
                        </>
                      )}
                    </BtnPrimary>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Recent Custom Generations
                      </h3>
                      <button
                        onClick={fetchCustomGenerationHistory}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Refresh
                      </button>
                    </div>
                    {isLoadingCustomHistory ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        <span className="ml-2 text-gray-500">Loading custom jobs...</span>
                      </div>
                    ) : customGenerationJobs.length ? (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-zinc-800 text-left text-gray-500 dark:text-gray-400 uppercase text-xs">
                            <tr>
                              <th className="px-4 py-3">Date Range</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3">Progress</th>
                              <th className="px-4 py-3">Filter</th>
                              <th className="px-4 py-3">Requested By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customGenerationJobs.map((job) => (
                              <tr
                                key={job._id || job.jobId}
                                className="border-t border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100"
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {formatJobDateRange(job)}
                                </td>
                                <td className="px-4 py-3">
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${getJobStatusBadgeClasses(job.status)}`}
                                  >
                                    {job.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  {job.progress?.processed || 0} / {job.progress?.total || 0}
                                </td>
                                <td className="px-4 py-3">{getJobFilterLabel(job)}</td>
                                <td className="px-4 py-3">
                                  {job.requestedBy?.email || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
                        No custom generation jobs found yet.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : jobStatus && jobStatus !== 'completed' ? (
            // Progress view
            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full">
                    <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                </div>

                <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  {jobStatus === 'pending'
                    ? activeGenerationMode === 'custom'
                      ? 'Preparing custom run...'
                      : 'Preparing monthly run...'
                    : activeGenerationMode === 'custom'
                      ? 'Generating custom date range...'
                      : 'Generating invoices...'}
                </h3>

                {jobProgress && (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Processing {jobProgress.processed} of {jobProgress.total} agents
                    </div>

                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div
                        className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${jobProgress.percentage || 0}%` }}
                      />
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{jobProgress.successful}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Successful</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{jobProgress.failed}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
                      </div>
                    </div>

                    {jobProgress.currentEntity && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Currently processing: {jobProgress.currentEntity}
                      </div>
                    )}
                  </div>
                )}

                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  You can close this modal and continue working. The generation will continue in the background.
                </p>

                <button
                  onClick={() => {
                    setIsGenerateModalOpen(false);
                    toast.info('Generation continues in background');
                  }}
                  className="mt-6 px-4 py-2 bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-zinc-600"
                >
                  Close (Continue in Background)
                </button>
              </div>
            </div>
          ) : generationResult ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Generation Results - {generationResult.data.period.label}
                  </h3>
                  {resultPeriodDetails?.startDate && resultPeriodDetails?.endDate && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Date Range:{' '}
                      {`${moment(resultPeriodDetails.startDate).format('DD MMM YYYY')} - ${moment(resultPeriodDetails.endDate).format('DD MMM YYYY')}`}{' '}
                      {resultPeriodDetails.totalDays ? `(${resultPeriodDetails.totalDays} days)` : ''}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Mode: {generationResult.mode === 'custom' ? 'Custom range' : 'Monthly run'} · Filter:{' '}
                    {resultFilterLabel}
                    {resultFilterNames.length > 0 && (
                      <>
                        {' '}
                        (
                        {resultFilterNames.join(', ')}
                        {generationResult.data.filter?.agentIds &&
                          generationResult.data.filter.agentIds.length > resultFilterNames.length
                          ? '...'
                          : ''}
                        )
                      </>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsGenerateModalOpen(false);
                    resetGenerationModalState();
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Processed</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {generationResult.data.summary.totalAgentsProcessed}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Successfully Generated</div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {generationResult.data.summary.successfullyGenerated}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Errors</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {generationResult.data.summary.errors}
                  </div>
                </div>
                <div className="bg-white dark:bg-zinc-800 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Amount</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(
                      generationResult.data.summary.totalInvoiceAmount,
                      generationResult.data.invoices[0]?.currency || 'USD'
                    )}
                  </div>
                </div>
              </div>

              {generationResult.data.invoices.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Generated Invoices ({generationResult.data.invoices.length})
                  </h4>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Invoice #</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Agent</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Amount</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generationResult.data.invoices.map((invoice, idx) => (
                          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-zinc-800">
                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{invoice.entityEmail}</td>
                            <td className="px-4 py-2 text-gray-900 dark:text-gray-100 font-medium">
                              {formatCurrency(invoice.amount, invoice.currency)}
                            </td>
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{invoice.billableHours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {generationResult.data.errors.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    Errors ({generationResult.data.errors.length})
                  </h4>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Agent Email</th>
                          <th className="px-4 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generationResult.data.errors.map((error, idx) => (
                          <tr key={idx} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-zinc-800">
                            <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{error.agentEmail}</td>
                            <td className="px-4 py-2 text-red-600 dark:text-red-400">{error.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <BtnPrimary
                  onClick={() => {
                    setIsGenerateModalOpen(false);
                    resetGenerationModalState();
                  }}
                >
                  Close
                </BtnPrimary>
              </div>
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Regeneration Confirmation Modal */}
      <Modal
        isOpen={showRegenerateConfirm}
        onRequestClose={() => setShowRegenerateConfirm(false)}
        title="Confirm Invoice Regeneration"
        width="500px"
      >
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                  Are you sure you want to regenerate invoices for{' '}
                  {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}?
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  This action will:
                </p>
                <ul className="mt-2 text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                  <li>Delete all existing invoices for this period</li>
                  <li>Generate new invoices with updated data</li>
                  <li>Reset all payment statuses</li>
                  <li>Send new notification emails</li>
                </ul>
                {selectedMonthForRegeneration && (
                  <p className="mt-3 text-sm font-medium text-yellow-600 dark:text-yellow-400">
                    This cannot be undone!
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={() => setShowRegenerateConfirm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-600"
            >
              Cancel
            </button>
            <BtnPrimary
              onClick={() => {
                handleGenerateMonthlyInvoices(true);
                setShowRegenerateConfirm(false);
              }}
              className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600"
            >
              <RefreshCw className="w-4 h-4" />
              Regenerate Invoices
            </BtnPrimary>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgentInvoice;
